/**
 * pullToRefresh — 당겨서 새로고침의 순수 임계/거리 계산 (#26).
 *
 * 제스처/DOM 배선은 `PullToRefresh` 컴포넌트가 소유한다. 여기 있는 것은 부수효과
 * 없는 순수 함수뿐이라 vitest로 검증한다 — 원시 당김거리(px)를 감쇠/클램프한
 * 표시 거리로, 그리고 임계 도달·진행도로 환산한다.
 */

/** 이 거리(px) 이상 당긴 뒤 놓으면 새로고침한다. */
export const PULL_THRESHOLD_PX = 72;

/** 표시 당김 거리 상한(px) — 고무줄처럼 더 당겨도 여기서 멈춘다. */
export const MAX_PULL_PX = 120;

/**
 * 당김 감쇠 계수(0~1). 손가락 이동량의 절반만 거리로 반영해 무겁게 끌리는
 * 느낌을 준다(네이티브 overscroll과 비슷).
 */
export const PULL_RESISTANCE = 0.5;

export interface PullDistanceOptions {
  resistance: number;
  maxPx: number;
}

/** 0~max 클램프. */
const clamp = (value: number, min: number, max: number): number => {
  if (value < min) {
    return min;
  }
  if (value > max) {
    return max;
  }
  return value;
};

/**
 * 원시 아래방향 당김(px) → 감쇠·클램프된 표시 거리(px).
 * 위로(0 이하) 당기면 0. NaN/음수 입력은 0으로 막는다.
 */
export const computePullDistance = (
  rawDeltaPx: number,
  { resistance, maxPx }: PullDistanceOptions = {
    resistance: PULL_RESISTANCE,
    maxPx: MAX_PULL_PX,
  },
): number => {
  if (!Number.isFinite(rawDeltaPx) || rawDeltaPx <= 0) {
    return 0;
  }
  return clamp(rawDeltaPx * resistance, 0, maxPx);
};

/** 표시 거리가 임계 이상인지 — 놓을 때 새로고침할지 판정. */
export const isPastThreshold = (
  distancePx: number,
  thresholdPx: number = PULL_THRESHOLD_PX,
): boolean => Number.isFinite(distancePx) && distancePx >= thresholdPx;

/** 임계까지의 진행도(0~1) — 인디케이터 회전/투명도용. */
export const pullProgress = (
  distancePx: number,
  thresholdPx: number = PULL_THRESHOLD_PX,
): number => {
  if (!Number.isFinite(distancePx) || distancePx <= 0 || thresholdPx <= 0) {
    return 0;
  }
  return clamp(distancePx / thresholdPx, 0, 1);
};

/**
 * 당김을 시작/유지할 조건 — 스크롤 최상단(scrollTop≤0)에서 아래로(deltaY>0)
 * 당기는 수직 제스처만. 가로 스크럽(touch-none 그래프)과 충돌하지 않는다.
 */
export const shouldEngagePull = (scrollTop: number, deltaY: number): boolean =>
  scrollTop <= 0 && deltaY > 0;
